<script lang="ts">
  import type { TemplateForGetAllResponse } from "$lib/api/template";
  import { ProfilePicture } from "$lib/components";
  import Tooltip from "$lib/components/basic/tooltip/tooltip.svelte";
  import { Button } from "$lib/components/ui";
  import { authStore } from "$lib/stores";
  import { cn, simpleHash } from "$lib/utility";
  import { BadgeCheck, Box, Pencil, Trash } from "lucide-svelte";

  const {
    name,
    description,
    categories,
    className,
    thumbnailUrl,
    isOfficial,
    creator,
    prepopulated = false,
    showInCreateDialog = false,
    showActions = false,
    createdBy,
    id,
    onDelete,
    onEdit,
  }: {
    name: string;
    description: string;
    thumbnailUrl: string;
    creator: TemplateForGetAllResponse["creator"];
    categories: { name: string; id: string }[];
    className?: string;
    isOfficial?: boolean;
    prepopulated?: boolean;
    showInCreateDialog?: boolean;
    showActions?: boolean;
    createdBy?: number;
    id: string;
    onDelete?: (id: string) => void;
    onEdit?: () => void;
  } = $props();

  const isAdmin = $derived(authStore.isAdmin());
  const isCreator = $derived($authStore.user?.id === createdBy);
  const isEditable = $derived(showActions && (isAdmin || isCreator));
</script>

<div class={cn("relative group flex w-full flex-col justify-between gap-2 rounded-lg p-4", className || "")}>
  {#if isEditable}
    <div class="absolute right-6 z-20 top-7 flex items-center justify-center gap-2">
      <Button
        onclick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onEdit?.();
        }}
        variant="ghost"
        size="icon"
        class="size-6 bg-white hover:bg-white/80 rounded-full"
      >
        <Pencil class="text-black size-3 stroke-1" />
      </Button>

      <Button
        onclick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDelete?.(id);
        }}
        variant="ghost"
        size="icon"
        class="size-6 bg-white hover:bg-white/80 rounded-full"
      >
        <Trash class="size-3 stroke-1 text-theme-red" />
      </Button>
    </div>
  {/if}

  <div class="relative overflow-hidden rounded-lg border h-[240px] border-sidebar-border dark:border-white/20">
    <img
      src={thumbnailUrl || "/gradients/grad-2.webp"}
      alt={name}
      class="dark:opacity-80 h-[240px] w-full object-cover group-hover:scale-125 transition-all duration-300"
    />
  </div>
  {#if isEditable && isAdmin}
    <div class="absolute bottom-4 right-4 flex items-center justify-center gap-2">
      {#if showInCreateDialog}
        <Tooltip content={showInCreateDialog ? "Show in create dialog" : "Not show in create dialog"}>
          <div class="rounded-base border border-theme-green-medium px-2 py-0.5 text-2xs text-theme-green-medium">
            S
          </div>
        </Tooltip>
      {/if}
      {#if prepopulated}
        <Tooltip content={prepopulated ? "Pre-populated" : "Not pre-populated"}>
          <div class="rounded-base border border-theme-green-medium px-2 py-0.5 text-2xs text-theme-green-medium">
            P
          </div>
        </Tooltip>
      {/if}
    </div>
  {/if}
  <div class="flex flex-col">
    <h3 class="flex items-center gap-2 text-base font-medium tracking-tight">
      <span class=" max-w-48 truncate">
        {name}
      </span>
      {#if isOfficial}
        <Tooltip content="Chatsheet official template">
          <BadgeCheck class="size-4 stroke-2 text-theme-green-deep" />
        </Tooltip>
      {/if}
    </h3>
    <div class="flex items-center gap-1">
      {#if creator.avatarUrl}
        <img src={creator.avatarUrl} alt={creator.displayName} class="size-4 rounded-full" />
      {:else}
        <ProfilePicture
          wrapperClass={cn(
            "px-0 bg-theme-background-tertiary mt-0 flex-shrink-0 transition-default size-4 border-sidebar-border border-1",
          )}
          imageUrl={`https://www.gravatar.com/avatar/${simpleHash(creator.email)}?d=identicon&s=64`}
          shape="circle"
          outline={true}
          altText="Collaborator"
        />
      {/if}
      <p class="text-2xs text-theme-text-secondary">{creator?.displayName || creator?.email}</p>
    </div>
    <p class="text-2xs text-theme-text-secondary line-clamp-3 mt-3">{description}</p>
  </div>
  <div class="mt-5 flex flex-col">
    <div class="flex flex-wrap gap-2">
      {#each categories as category}
        <p class="rounded-base bg-theme-gray-100 px-2 py-1 text-2xs text-theme-text-secondary dark:bg-theme-gray-800">
          {category.name}
        </p>
      {/each}
    </div>
  </div>
</div>
